import EventBus from '@core/eventbus';

type GenericFn = (...args: unknown[]) => unknown;

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed
 * since the last time the debounced function was invoked.
 *
 * @param func - The function to debounce.
 * @param wait - The number of milliseconds to delay.
 * @param immediate - If `true`, the function triggers immediately and then waits for the interval before being called again.
 * @return The new debounced function.
 */
export function debounce<T extends GenericFn>(func: T, wait = 0, immediate = false, context = this): T {
  let timeout: NodeJS.Timeout;

  return function (...args: Parameters<T>): ReturnType<T> {
    clearTimeout(timeout);

    timeout = setTimeout(function () {
      timeout = null;
      if (!immediate) return func.apply(context, args);
    }, wait);

    if (immediate && !timeout) return func.apply(context, args);
  } as T;
}

export const frameDebounce = <T extends GenericFn>(func: T, immediate = false, context = this): T => {
  let raf: number;

  return function (...args: Parameters<T>): ReturnType<T> {
    if (raf) {
      return;
    }

    raf = requestAnimationFrame(() => {
      cancelAnimationFrame(raf);
      raf = undefined;
      if (!immediate) return func.apply(context, args);
    });

    if (immediate && !raf) return func.apply(context, args);
  } as T;
};

export const throttle = <T extends GenericFn>(callback: T, threshold: number, context = this) => {
  let deferTimer: NodeJS.Timeout, lastRun: number;

  return function () {
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    const now = +new Date();

    if (lastRun && now < lastRun + threshold) {
      // hold on to it
      clearTimeout(deferTimer);
      deferTimer = setTimeout(() => {
        lastRun = now;
        callback.apply(context, args);
      }, threshold);
    } else {
      lastRun = now;
      callback.apply(context, args);
    }
  } as T;
};

export default class D3Throttle<T extends GenericFn = GenericFn> {
  enabled: boolean;
  events: string[];
  target: HTMLElement | Window;
  throttledFn: T;

  /**
   * Create a new throttle
   * @param target - DOM target of the events
   * @param callback - callback function
   * @param threshold - threshold in millisecond
   * @param events - event names
   * @param scope - scope of the callback function
   */
  constructor(
    target: HTMLElement | Window,
    callback: T,
    threshold = 250,
    events: string[] = [],
    scope: any = undefined,
  ) {
    if (!callback || typeof callback !== 'function') throw new Error('callback must be a function!!');
    if (!Array.isArray(events)) throw new Error('events parameter must be an Array!!');

    this.enabled = true;

    if (!target) target = window;

    this.events = events;
    this.target = target;

    this.throttledFn = throttle(callback, threshold, scope ?? this);
    events.forEach((event) => {
      EventBus.on(event, this.run, this);

      this.addEventListenerWithNamespace(
        event,
        () => {
          if (!this.enabled) return;
          this.run();
        },
        this.target,
      );
    });
  }

  eventsFunctionMap = {};

  addEventListenerWithNamespace(event: string, func: () => void, target: HTMLElement | Window | Document = document) {
    this.eventsFunctionMap[event] = func;
    target.addEventListener(event.split('.')[0], this.eventsFunctionMap[event]);
  }

  removeEventListenerWithNamespace(event: string, target: HTMLElement | Window | Document = document) {
    target.removeEventListener(event.split('.')[0], this.eventsFunctionMap[event]);
    delete this.eventsFunctionMap[event];
  }

  run() {
    this.throttledFn();
  }

  off() {
    this.enabled = false;

    if (this.events) {
      this.events.forEach((event) => {
        EventBus.off(event, this.run, this);
        this.removeEventListenerWithNamespace(event, this.target);
      });
    }
  }

  static debounce(fn, wait, immediate, context = this) {
    return debounce(fn, wait, immediate, context);
  }
}
